import React, { useState } from "react";
import PropTypes from "prop-types";
import { List } from "antd";
import TableWithControls from "../common/table-with-controls";
import LogoROI from "../common/logo-roi";
import SaveAsPortfolio from "./save-as-portfolio";
import { isFinite } from "lodash";
import { getPatents, esqPatents, PATENTS_SEARCH_URL } from "../../services/api-patents";
import { toSetString, toDateString, toPatentDetailLink, toUSDCurrency } from "../../helpers/converters";
import { diversifyHighlight } from "../../helpers/table";

const TABLE_DEFAULT_COLUMNS = ["publication_number", "publication_date", "assignee_current", "rating_broadness", "rating_citation", "rating_litigation", "portfolio_score", "rnix_score", "title"];

const PatentTable = ({ filter, accessLevel }) => {
  const [queries, setQueries] = useState({
    sort: filter.opposition_country ? "-opposition_filing_date" : "-portfolio_score",
    ...filter
  });
  const onQueryChange = queriesNew => setQueries(({
    sort: filter.opposition_country ? "-opposition_filing_date" : "-portfolio_score",
    ...queriesNew
  }));
  const fetcher = q => getPatents({ ...q }).then(json => ({
    total: json.hits.total.value,
    data: json.hits.hits.map(e => ({ ...e._source, highlight: e.highlight }))
  }))
  const allCols = [
    { dataIndex: "title", title: "Title", width: 480 },
    {
      dataIndex: "family_id",
      title: "Family ID",
      width: 120,
      render: val => val === "-1" ? "N/A" : val
    },
    {
      dataIndex: "extended_family_id",
      title: "Extended Family ID",
      width: 160,
      render: val => val === "-1" ? "N/A" : val
    },
    { dataIndex: "grant_number", title: "Patent Number", width: 160 },
    {
      dataIndex: "publication_number",
      title: "Publication",
      width: 180,
      render: val => toPatentDetailLink(val)
    },
    { dataIndex: "spif_pub", title: "Publication Number - SPIF", width: 180 },
    { dataIndex: "spif_app", title: "Application Number - SPIF", width: 180 },
    { dataIndex: "application_number", title: "Application", width: 200 },
    { dataIndex: "inventors", title: "Inventor", width: 200, render: toSetString },
    { dataIndex: "examiner", title: "Examiner", width: 200, render: toSetString },
    { dataIndex: "law_firm", title: "Law Firm", width: 200, render: toSetString },
    { dataIndex: "assignee_current", title: "Current Assignee", width: 200, render: toSetString },
    { dataIndex: "assignee_original", title: "Original Assignee", width: 200, render: toSetString },
    { dataIndex: "assignee_parent", title: "Parent Company", width: 200, render: toSetString },
    {
      dataIndex: "application_date",
      title: "Filing Date",
      width: 145,
      render: toDateString,
      key: "application_date",
      sorter: true
    },
    { dataIndex: "expiration_date", title: "Expiration Date", width: 140, render: toDateString, key: "expiration_date", sorter: true },
    { dataIndex: "publication_date", title: "Publication Date", width: 145, render: toDateString, key: "publication_date", sorter: true },
    { dataIndex: "priority_date", title: "Priority Date", width: 140, render: toDateString, key: "priority_date", sorter: true },
    { dataIndex: "rating_broadness", title: "BRIX", width: 80, key: "rating_broadness", sorter: true },
    { dataIndex: "rating_citation", title: "CITX", width: 80, key: "rating_citation", sorter: true },
    { dataIndex: "rating_litigation", title: "RUBY", width: 80, key: "rating_litigation", sorter: true },
    {
      dataIndex: "portfolio_score",
      title: "PVIX",
      width: 80,
      render: val => (val ? val.toFixed(2) : "0.00"),
      key: "portfolio_score",
      sorter: true
    },
    {
      dataIndex: "annuities",
      title: "Lifetime Renewal Fees",
      width: 80,
      key: "annuities",
      sorter: true,
      render: val => (isFinite(val) ? toUSDCurrency(Math.round(val / 10) * 10) : "")
    },
    {
      dataIndex: "norm_family_annuities",
      title: "SVIX",
      width: 80,
      key: "norm_family_annuities",
      sorter: true,
      render: val => (isFinite(val) ? val.toFixed(2) : "0.00")
    },
    {
      dataIndex: "rnix_score",
      title: "RNIX",
      width: 80,
      key: "rnix_score",
      sorter: true,
      render: val => (isFinite(val) ? val.toFixed(2) : "")
    },
    { dataIndex: "publication_status", title: "Status", width: 120 },
    { dataIndex: "publication_type", title: "Type", width: 120 },
    { dataIndex: "country", title: "Country", width: 100 },
    { dataIndex: "doc_category", title: "Category", width: 120 },
    {
      dataIndex: "transaction_type",
      title: (
        <span>
          {"Transaction "}
          <LogoROI />
        </span>
      ),
      width: 180,
      key: "transaction_type",
      sorter: true,
      render: (text, record) => <a href={record.transaction_ro_url}>{text}</a>
    },
    { dataIndex: "transaction_report_code", title: "Report Code", width: 120 },
    { dataIndex: "kind_code", title: "Kind Code", width: 120 },
    { dataIndex: "cpc_codes", title: "CPC Codes", width: 120, render: toSetString },
    { dataIndex: "uspc_codes", title: "USPC Codes", width: 120, render: toSetString },
  ];
  const highlightFieldName = {
    description: "DESCRIPTION",
    claims: "CLAIMS",
    abstract_fulltext: "ABSTRACT",
    title: "TITLE",
    non_patent_citations: "CITATIONS"
  }
  const expandable = queries.full_text ? {
    defaultExpandAllRows: true,
    expandedRowRender: record => (
      <List
        dataSource={diversifyHighlight(Object.keys(highlightFieldName))(record)}
        renderItem={d => (
          <List.Item>
            <div dangerouslySetInnerHTML={{ __html: `[${highlightFieldName[d.field]}] ${d.highlight}` }} />
          </List.Item>
        )}
        size="small"
      />
    )
  } : undefined;
  return (
    <TableWithControls
      queries={queries}
      fetcher={fetcher}
      onChange={onQueryChange}
      columns={allCols}
      columnKeysSelected={TABLE_DEFAULT_COLUMNS}
      rowKey="publication_number"
      tableKey="landscapeTable"
      expandable={expandable}
      size="small"
      shouldSelectColumns={accessLevel === "full"}
      shouldExport={accessLevel === "full"}
      exportESUrl={PATENTS_SEARCH_URL}
      exportESQueryBuilder={esqPatents}
      customFloatOut={<SaveAsPortfolio filter={filter} />}
    />
  )
};

PatentTable.propTypes = {
  filter: PropTypes.object.isRequired,
  accessLevel: PropTypes.string.isRequired
};

export default PatentTable;
